import { useSyncExternalStore } from 'react';

/** 見切れ表示用ツールチップの識別ID。ホスト(TruncatedTooltipHost)と対象要素(tooltipProps)で共有する */
export const TRUNCATED_TOOLTIP_ID = "common-truncated-tooltip";

/**
 * 要素の文字が枠からはみ出して「...」になっている場合だけ、その全文を返す。見切れていなければ null。
 */
export const getTruncatedText = (element: HTMLElement | null): string | null => {
  if (!element) return null;
  const isTruncated = element.scrollWidth > element.clientWidth;
  return isTruncated && element.textContent ? element.textContent : null;
};

//----------------------------------------------------------------
// いまホバーされている「見切れ文字の全文」（アプリ全体で1つだけ持つ）
//----------------------------------------------------------------
// 以前は行コンポーネントごとに useState で持ち、行ごとに <Tooltip> を描画していた。
// それだと同一idの Tooltip が行数N個になり、各 Tooltip が全行の対象要素へリスナーを付けにいくため
// 処理量が N² になる（170件前後で画面が応答不可になった: P-0060）。
// 状態をここ（モジュール内の1つ）に持ち、Tooltip は TruncatedTooltipHost の1個だけにする。
let activeTooltipText: string | null = null;
const listeners = new Set<() => void>();

const setActiveTooltipText = (text: string | null) => {
  if (activeTooltipText === text) return;
  activeTooltipText = text;
  listeners.forEach((listener) => listener());
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};
const getSnapshot = () => activeTooltipText;

// マウスホバー時の判定ロジック（全ての対象要素で共通・参照が変わらない関数）
const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
  setActiveTooltipText(getTruncatedText(e.currentTarget));
};
const handleMouseLeave = () => {
  setActiveTooltipText(null);
};

/**
 * いまホバーされている見切れ文字の全文（無ければ null）。ツールチップ本体(TruncatedTooltipHost)が購読する。
 */
export const useActiveTruncatedText = (): string | null =>
  useSyncExternalStore(subscribe, getSnapshot);

/**
 * ✂️ 文字が見切れている場合のみツールチップを表示するための、対象要素用の props を返すフック
 * * @description
 * 要素の幅からはみ出して「...」になっているテキストを、マウスホバー時に全文のツールチップで表示します。
 * ツールチップ自体は CommonLayout の <TruncatedTooltipHost /> が1個だけ描画するので、行側では何も置かなくてよい。
 * * @example
 * // 1. 基本的な使い方
 * const { tooltipProps } = useTruncatedTooltip();
 * return <p {...tooltipProps}>{item.text}</p>
 * * @example
 * // 2. デザインを変更する場合
 * // ⚠️ classNameを上書きする場合は、必ず 'truncate' を書き直してください！
 * // truncateがないと省略判定ができず、ツールチップが出ません。
 * <p
 * {...tooltipProps}
 * className="text-blue-500 text-sm truncate" // ← truncate 必須！
 * >
 * {item.text}
 * </p>
 * * @param baseClass - 適用するCSSクラス（デフォルト: "font-semibold truncate"）
 */
export const useTruncatedTooltip = (
  baseClass: string = "font-semibold truncate"
) => {
  // ターゲット要素に渡すProps
  const tooltipProps = {
    className: baseClass,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    "data-tooltip-id": TRUNCATED_TOOLTIP_ID,
  };

  return {
    tooltipProps, // <p>タグにつけるやつ
    // 旧API互換。ツールチップは TruncatedTooltipHost が1個だけ描画するので、行側では何も描画しない（常に null）。
    TooltipElement: null,
  };
};
